import React from "react";
import { useState } from "react";
import { TabsContent } from "@radix-ui/react-tabs";
import FormInputField from "@/components/lib/from-input-field";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { toast } from "react-hot-toast";
import { updatePin } from "@/api/new-api/utility";
import useAuth from "@/hooks/useAuth";

const securitySchema = z
  .object({
    currentPin: z
      .string()
      .min(4, "Current PIN must be 4–5 digits")
      .max(5, "Current PIN can't be more than 5 digits"),
    newPin: z
      .string()
      .min(4, "New PIN must be 4–5 digits")
      .max(5, "New PIN can't be more than 5 digits"),
    confirmPin: z
      .string()
      .min(4, "Confirm PIN must be 4–5 digits")
      .max(5, "Confirm PIN can't be more than 5 digits"),
  })
  .refine((data) => data.newPin === data.confirmPin, {
    message: "New PIN and Confirm PIN must match",
    path: ["confirmPin"],
  });

function Security() {
  const form = useForm({
    resolver: zodResolver(securitySchema),
    mode: "onBlur",
    defaultValues: {
      currentPin: "",
      newPin: "",
      confirmPin: "",
    },
  });
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const update = async (data) => {
    setLoading(true);
    try {
      const payload = {
        staff_id: user?.id,
        current_pin: data?.currentPin,
        new_pin: data?.newPin,
      };

      const res = await updatePin(payload);
      if (res?.status === "success") {
        toast.success("PIN updated successfully!");
      } else {
        toast.error(res?.message || "Failed to update PIN");
      }
      form.reset();
      setLoading(false);
    } catch (error) {
      toast.error("Something went wrong while updating PIN");
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <TabsContent value="security">
      <div className="flex flex-col gap-5 px-4 py-6">
        <div className="space-y-1">
          <h2 className="text-cwhite2">Change PIN</h2>
          <p className="text-cwhite3/70">
            Remember, your PIN is your digital key to your account. Keep it safe
            and secure!
          </p>
        </div>

        <Form {...form}>
          <form
            className="max-w-sm w-full space-y-8"
            onSubmit={form.handleSubmit(update)}
          >
            <FormInputField
              name="currentPin"
              placeholder="•••••"
              form={form}
              type="password"
              style="bg-cblack3"
              label="Current PIN"
            />
            <FormInputField
              name="newPin"
              placeholder="•••••"
              form={form}
              type="password"
              style="bg-cblack3"
              label="New PIN"
            />
            <FormInputField
              name="confirmPin"
              placeholder="•••••"
              form={form}
              type="password"
              style="bg-cblack3"
              label="Confirm New PIN"
            />

            <Button
              isLoading={loading}
              className="bg-cgreen w-full"
              type="submit"
            >
              Update
            </Button>
          </form>
        </Form>
      </div>
    </TabsContent>
  );
}

export default Security;
